import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { useSelector, useDispatch } from "react-redux";
import { StyledModal } from "@aureskonnect/react-ui";
import { ModalBody } from "reactstrap";

import ErrorToast from "@components/Common/ErrorTost";
import { store } from "@components/VerticalLayout/store";
import { getUuidUser } from "@helpers/getUuidUser";
import { EtkTable } from "@components/Common/EtkTable";

import { useAuthHeader } from "../../../../../api/headersKeycloak";
import { useFetchGlobal } from "../../../../../api/hooks";
import { RootState, AppDispatch } from "../../../../../redux/store";
import {
    setIsAssociateModalOpened,
    setIsAssociatedFidelity,
    setSelectedRow,
    setIds,
    setMessage,
} from "../../../../../redux/slices/fidelitySlice";

import { countMatchingIds, getArchivedFidelityIds } from "../../helpers";

import { ConfirmationMessage } from "../ConfirmationMessageWrapper";
import { ConfirmationUpdate } from "../ConfirmationUpdateWrapper";

import { FidelityAssociateModalProps } from "./types";
import { useEtkColumns } from "./useEtkColumns";
import { useAssociateModalEffects } from "./useAssociateModalEffects";
import { useRunAssociate } from "./useRunAssociate";
import AssociateModalHeader from "./components/AssociateModalHeader";
import AssociateModalFooter from "./components/AssociateModalFooter";

import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";

const SHOP_ID_DEFAULT = 0;

function buildUrl(
    localShop: string[],
    userID: number,
    franchiseID: string
): string {
    const base = process.env.REACT_APP_API_V2_URL;
    if (localShop.length === 1) {
        return `${base}/settings/general/list/fidelity?userId=${userID}&shopId=${localShop[0]}&franchiseId=${franchiseID}`;
    }
    return `${base}/settings/general/fidelity?userId=${userID}&shopId=${SHOP_ID_DEFAULT}&franchiseId=${franchiseID}&type=activated`;
}

export function FidelityAssociateModalWrapper({
    setData,
    selectedShopsRows,
    setLimit,
}: FidelityAssociateModalProps) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const getAuthHeader = useAuthHeader();

    const isAssociateModalOpened = useSelector(
        (s: RootState) => s.fidelity.isAssociateModalOpened
    );
    const localShop = useSelector((s: RootState) => s.fidelity.localShop);
    const isAssociateModalClosed = useSelector(
        (s: RootState) => s.fidelity.isAssociateModalClosed
    );
    const idsLocalShop = useSelector(
        (s: RootState) => s.fidelity.idsLocalShop
    );
    const rowsShop = useSelector((s: RootState) => s.fidelity.rowsShop);
    const selectedRowsFidelity = useSelector(
        (s: RootState) => s.fidelity.selectedRowsFidelity
    );
    const activeFidelity = useSelector(
        (s: RootState) => s.fidelity.activeFidelity
    );
    const isAssociatedFidelity = useSelector(
        (s: RootState) => s.fidelity.isAssociatedFidelity
    );
    const fidelity = useSelector((s: RootState) => s.fidelity.fidelity);
    const dispatch = useDispatch<AppDispatch>();
    const { userID, franchiseID, operatorID, shopID } = useSnapshot(store);

    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const uuidUser = getUuidUser();

    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
    const [dataIsUpdated, setDataIsUpdated] = React.useState<boolean | number>(
        false
    );
    const [isValidateClickedModal, setIsValidateClickedModal] = React.useState<
        boolean | number
    >(false);
    const [isStateChanged, setIsStateChanged] = React.useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [selectedFidelity, setSelectedFidelity] = React.useState<any[]>([]);
    const [
        isValidateButtonEnabled,
        setIsValidateButtonEnabled,
    ] = React.useState<boolean>(true);

    const url = buildUrl(localShop as string[], userID, franchiseID);
    const customColumns = useEtkColumns({
        t,
        selectedShopsRows,
        selectedFidelity,
    });

    const requestHeaders = React.useMemo(
        () =>
            ({ "Accept-Language": i18nextLng, ...getAuthHeader } as Record<
                string,
                string
            >),
        [i18nextLng, getAuthHeader]
    );

    useAssociateModalEffects({
        selectedShopsRows,
        selectedRowsFidelity,
        activeFidelity: activeFidelity as any[],
        rowsShop: rowsShop as any[],
        localShop: localShop as string[],
        setSelectedFidelity,
        setIsStateChanged,
    });

    const associateFidelity = useRunAssociate({
        userID,
        franchiseID,
        operatorID,
        shopID,
        uuidUser,
        localShop: localShop as string[],
        rowsShop: rowsShop as any[],
        selectedRowsFidelity,
        activeFidelity: activeFidelity as any[],
        fidelity,
        isAssociateModalClosed,
        fetchGlobal,
        t,
        setLimit,
        setIsValidateButtonEnabled,
    });

    React.useEffect(() => {
        if (isValidateClickedModal === true) {
            associateFidelity();
        }
        // eslint-disable-next-line
    }, [isValidateClickedModal]);

    const closeModal = () => {
        dispatch(setIsAssociateModalOpened(false));
        setData(true);
    };

    const handleValidate = () => {
        const archived = getArchivedFidelityIds(rowsShop as any[]);
        dispatch(setIds(countMatchingIds(selectedRows, idsLocalShop as string[])));

        if (fidelity === "" || fidelity === undefined) {
            ErrorToast(
                t("Attention ! You must select least one fidelity")
            );
            return;
        }
        if (isStateChanged) {
            setIsModalOpened(true);
            return;
        }
        dispatch(setSelectedRow(selectedRows));
        if (selectedRowsFidelity && archived.includes(selectedRowsFidelity.Id)) {
            dispatch(setIsAssociatedFidelity(true));
            dispatch(
                setMessage(
                    t(
                        "Attention! One or more selected fidelity are already assigned and archived at the store level.Would you unarchive them?"
                    )
                )
            );
        } else {
            associateFidelity();
        }
    };

    return (
        <StyledModal
            toggle={true}
            isOpen={isAssociateModalOpened!}
            centered
            className="modal-wrapper__clz"
            backdrop="static"
            fade={false}
            scrollable
        >
            <AssociateModalHeader onClose={closeModal} />
            <ModalBody>
                <EtkTable
                    refreshKey={`${dataIsUpdated}`}
                    url={url}
                    headers={requestHeaders}
                    customColumns={customColumns}
                    canSelect={false}
                    customSelect
                    onSelectionChange={setSelectedRows}
                    showSearch
                    canResize
                    canMovedCheckboxLeftOnExpand
                    dataIsUpdated={dataIsUpdated}
                    setDataIsUpdated={setDataIsUpdated}
                    minHeight="500px"
                    maxHeight="500px"
                    name="associateFidelity"
                />
            </ModalBody>
            <AssociateModalFooter
                isValidateButtonEnabled={isValidateButtonEnabled}
                onCancel={closeModal}
                onValidate={handleValidate}
            />
            {isModalOpened ? (
                <ConfirmationMessage
                    msg={t(
                        "Warning! This change will affect specific settings in the store(s).\n\nDo you want to confirm the modification?"
                    )}
                    setIsModalOpened={setIsModalOpened}
                    isModalOpened={isModalOpened}
                    setIsValidateClickedModal={setIsValidateClickedModal}
                />
            ) : null}
            {isAssociatedFidelity ? (
                <ConfirmationUpdate
                    isModalOpened={isAssociatedFidelity}
                    setIsModalOpened={(v: boolean) =>
                        dispatch(setIsAssociatedFidelity(v))
                    }
                    setIsValidateClickedModal={setIsValidateClickedModal}
                />
            ) : null}
        </StyledModal>
    );
}
